import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DefaultActionButtons from 'components/DefaultActionButtons';
import CheckboxField from 'components/formFields/CheckboxField';
import SectionTitle from 'components/typography/SectionTitle';
import {
  showSuccessFeedbackBar,
  showErrorFeedbackBar,
} from 'reducers/ui/uiSlice';
import {
  updateMarketingPreferences,
  getMarketingPreferences,
} from 'services/ClientService';
import { getSavedGroupTags, getBrands } from 'services/CommonService';
import {
  UPDATE_SUCCESS,
  UPDATE_ERROR,
} from 'shared/constants/feedbackMessages';
import { slugifyString, isWCAGContrastLow } from 'shared/helpers/common';

const getChannelsObjectValue = (valueList = []) => {
  const channelsObj = {};

  valueList.forEach((value) => {
    channelsObj[value] = true;
  });

  return channelsObj;
};

const getBrandObjectValues = (brands = []) => {
  return brands
    .filter(item => item.brand)
    .map(item => ({
      brand: item.brand,
      channels: item.channels.reduce((acc, channel) => {
        return { ...acc, [channel]: true };
      }, {}),
    }));
};

const getChannelsArrayValue = (objectValue = {}) => {
  const channelsArr = [];

  Object.keys(objectValue).forEach((channel) => {
    if (objectValue[channel]) {
      channelsArr.push(channel);
    }
  });

  return channelsArr;
};

const getMarketingChannels = (key, marketingTags = []) => {
  const channels = marketingTags.find((item) => item.key === key);

  if (channels) {
    return channels?.values || [];
  }

  return [];
};

export default function MarketingTab({ userData = {} }) {
  const dispatch = useDispatch();
  const { textConfig = {} } = useSelector((state) => state.ui);
  const [channelLabels, setChannelLabels] = useState([]);
  const [channelValues, setChannelValues] = useState({});
  const [originalChannelValues, setOriginalChannelValues] = useState({});
  const [allBrands, setAllBrands] = useState([]);
  const [brandLabels, setBrandLabels] = useState([]);
  const [brandValues, setBrandValues] = useState([]);
  const [originalBrandValues, setOriginalBrandValues] = useState([]);
  const [isUpdated, setIsUpdated] = useState(false);
  const [isTablet, setIsTablet] = useState(window.innerWidth < 768);

  const loadData = useCallback(async () => {
    const MARKETING_TAG = 'Opt in types';
    try {
      const { data } = await getBrands();
      setAllBrands(data?.results);

      const { data: marketingChannelsList } = await getSavedGroupTags([
        MARKETING_TAG,
      ]);
      setChannelLabels(
        getMarketingChannels('Channels', marketingChannelsList[MARKETING_TAG]),
      );
      setBrandLabels(
        getMarketingChannels('Brands', marketingChannelsList[MARKETING_TAG]),
      );

      const { data: { defaultChannels = [], brands = [] } = {} } =
        await getMarketingPreferences(
          userData?._id, // eslint-disable-line no-underscore-dangle
        );

      const channelObjValues = getChannelsObjectValue(defaultChannels);
      setChannelValues(channelObjValues);
      setOriginalChannelValues(channelObjValues);

      const brandObjValues = getBrandObjectValues(brands);
      setBrandValues(brandObjValues);
      setOriginalBrandValues(brandObjValues);
    } catch (error) {
      setChannelValues([]);
      console.error(error);
    }
  }, [userData._id]); // eslint-disable-line no-underscore-dangle

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onWindowResize = () => {
    setIsTablet(window.innerWidth < 768);
  };

  useEffect(() => {
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
  }, []);

  const onChangeHandler = (brandId = null) => ({ target: { name, checked } }) => {
    setIsUpdated(true);
  
    if (brandId) {
      setBrandValues((prevValues) => {
        const brandExists = prevValues.some((brandItem) => brandItem.brand.id === brandId);
  
        if (brandExists) {
          return prevValues.map((brandItem) =>
            brandItem.brand.id === brandId
              ? {
                  ...brandItem,
                  channels: {
                    ...brandItem.channels,
                    [name]: checked,
                  },
                }
              : brandItem
          );
        } 

        return [
          ...prevValues,
          {
            brand: { id: brandId },
            channels: { [name]: checked },
          },
        ];

      });
    } else {
      setChannelValues((currentValue) => ({
        ...currentValue,
        [name]: checked,
      }));
    }
  };  

  const onCancel = () => {
    setIsUpdated(false);
    setChannelValues(originalChannelValues);
    setBrandValues(originalBrandValues);
  };

  const onSave = async () => {
    setIsUpdated(false);

    try {
      const { data: { defaultChannels = [], brands = [] } = {} } =
        await updateMarketingPreferences(
          userData?._id, // eslint-disable-line no-underscore-dangle
          {
            defaultChannels: getChannelsArrayValue(channelValues),
            brands: brandValues.map(({ brand, channels }) => ({
              brand,
              channels: getChannelsArrayValue(channels),
            })),
          },
        );

      const channelObjValues = getChannelsObjectValue(defaultChannels);
      setChannelValues(channelObjValues);
      setOriginalChannelValues(channelObjValues);

      const brandObjValues = getBrandObjectValues(brands);
      setBrandValues(brandObjValues);
      setOriginalBrandValues(brandObjValues);

      dispatch(showSuccessFeedbackBar(UPDATE_SUCCESS));
    } catch (error) {
      console.error(error);
      dispatch(showErrorFeedbackBar(UPDATE_ERROR));
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SectionTitle label="Marketing Preferences" />

      {textConfig?.marketingPreferencesUpperText && (
        <div className="text-gray-600 mt-4">
          {textConfig.marketingPreferencesUpperText}
        </div>
      )}

      <div className={`flex justify-between mt-[40px] border-b ${isTablet ? 'flex-col items-start' : 'items-center'}`}>
        <div className="text-xl font-medium min-w-[110px] text-center mr-6">Contact Channels</div>
        <div className={`flex flex-wrap ${isTablet ? 'flex-col' : ''}`}>
          {channelLabels.map((channel, index) => {
            const value = slugifyString(channel);
            return (
              <div key={value || index} className="bg-white rounded-t-lg">
                <CheckboxField
                  name={value}
                  containerClassName="m-4 flex items-center"
                  label={channel}
                  checked={channelValues?.[value]}
                  onChange={onChangeHandler()}
                />
              </div>
            );
          })}
        </div>
      </div>
      
      {allBrands.map((brandObj, i) => (
        <div 
          key={brandObj.id || i}
          className={`flex justify-between mt-[40px] border-b ${isTablet ? 'flex-col items-start' : 'items-center'}`}
        >
          <div
            style={{ 
              backgroundColor: brandObj?.hexColour || "black", 
              color: "white", 
              textShadow: brandObj?.hexColour && isWCAGContrastLow(brandObj.hexColour)
                ? "1px 1px 0 gray, -1px -1px 0 gray, 1px -1px 0 gray, -1px 1px 0 gray"
                : "none",
            }}
            className="text-xl font-medium inline-block p-2 rounded-md border-2 border-gray-500 min-w-[110px] text-center mr-6"
          >
            {brandObj.name}
          </div>
          
          <div className={`flex flex-wrap ${isTablet ? 'flex-col' : ''}`}>
            {brandLabels.map((channel, index) => {
              const value = slugifyString(channel);
              return (
                <div key={value || index} className="bg-white rounded-t-lg">
                  <CheckboxField
                    name={value}
                    containerClassName="m-4 flex items-center"
                    label={channel}
                    checked={
                      brandValues.find((b) => b.brand.id === brandObj.id)?.channels?.[value] || false
                    }                    
                    onChange={onChangeHandler(brandObj.id)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {textConfig?.marketingPreferencesMidText && (
        <div className="text-gray-600 mt-6 mb-4">
          {textConfig.marketingPreferencesMidText}
        </div>
      )}

      <div className="flex-grow" />

      {textConfig?.marketingPreferencesFooterText && (
        <div className="text-gray-600 mt-6 mb-4 text-center">
          {textConfig.marketingPreferencesFooterText}
        </div>
      )}

      {isUpdated && (
        <div className="sticky bottom-0 left-0 w-full bg-white border-t shadow-md p-4 z-10">
          <DefaultActionButtons
            onSecondaryButtonClick={onCancel}
            onPrimaryButtonClick={onSave}
            containerClassName="flex justify-end"
            secondaryButtonClassName="px-4"
            primaryButtonClassName="px-4"
          />
        </div>
      )}
    </div>
  );
}
